import React, { Suspense } from "react";
import { heroData, items } from "../data/heroData";

// Lazy load heavy components to improve LCP
const BentoGrid = React.lazy(() =>
  import("./ui/bento-grid").then(mod => ({ default: mod.BentoGrid }))
);
const BentoGridItem = React.lazy(() =>
  import("./ui/bento-grid").then(mod => ({ default: mod.BentoGridItem }))
);

const Hero = () => {
  return (
    <main className="bg-white">
      <header className="relative w-full h-[95vh] overflow-hidden">
        <img
          src={heroData.overlay.image}
          srcSet={`${heroData.overlay.imageSmall || heroData.overlay.image} 800w, ${heroData.overlay.image} 1600w`}
          sizes="100vw"
          alt="Header Banner"
          width="1920"
          height="1080"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/placeholder-profile.png";
          }}
        />

        <div className="absolute top-[70%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-6 text-center">
          <div className="text-center space-y-4">
            <span className="inline-block rounded-full bg-yellow-100 text-yellow-800 font-heading text-sm font-semibold px-4 py-1 border border-yellow-300 shadow-sm">
              {heroData.highlight.badge}
            </span>
            <h1 className="px-4 py-2 rounded-xl text-white font-heading font-bold text-4xl md:text-6xl drop-shadow-sm">
              {heroData.highlight.headline}
            </h1>
            <p className="text-lg font-semibold text-transparent bg-clip-text drop-shadow-sm font-heading text-white">
              Deutschlands ältester Angelverein –{" "}
              <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent animate-shimmer">
                seit 1866
              </span>
            </p>
<a
  href="https://scheinefuervereine.rewe.de#verein-10000069179?ecid=cop_widget_Q3-26-SFV_toolkit_widget_nn_nn_nn"
  target="_blank"
  rel="noopener noreferrer"
  title="Zum Profil des Vereins Angelfreunde 1866 Berlin e. V. auf der Scheine für Vereine Aktionswebsite"
  className="
    group
    mx-auto
    mt-6
    flex
    w-fit
    items-center
    justify-center
    overflow-hidden
    rounded-lg
    p-1
    shadow-lg
    transition-all
    duration-300
    ease-out
    hover:bg-red-600
    hover:shadow-xl
    hover:ring-red-400/50
    focus-visible:outline-none
    focus-visible:ring-2
    focus-visible:ring-red-500
    focus-visible:ring-offset-2
  "
>
  <img
    src="https://scheinefuervereine.rewe.de/widget/sfv_widget_240x45px.png"
    alt="REWE Scheine für Vereine – Angelfreunde 1866 Berlin e. V."
    width="240"
    height="45"
    loading="lazy"
    decoding="async"
    className="
      block
      h-auto
      w-[240px]
      transition-transform
      duration-300
      rounded-lg
      group-hover:scale-[1.02]
    "
  />
</a>          </div>
        </div>

        {/* Logo and Sponsor */}
        <figure className="absolute bottom-20 left-4 sm:left-8 md:left-20 w-20 sm:w-28 md:w-32 lg:w-40 h-auto">
          <img
            src={heroData.logo.src}
            alt={heroData.logo.alt}
            width="160"
            height="160"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder-profile.png";
            }}
            className="w-full h-auto p-2"
          />
        </figure>
      </header>

      {/* Main Hero Section */}
      <section
        className="py-12 sm:py-16"
        style={{
          backgroundImage: `url('/gradient_bg.svg'), linear-gradient(to bottom, #0ea5e9, #ffffff 50%, #ffffff)`,
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "cover, cover",
          backgroundPosition: "center, center",
        }}
      >
        <div className="mx-auto max-w-6xl px-6 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start">
            <header className="text-left">
              <h2 className="text-base font-semibold text-sky-600 font-heading">
                {heroData.section.title}
              </h2>
              <h3 className="mt-2 text-4xl font-bold font-heading tracking-tight text-gray-950 sm:text-5xl">
                {heroData.section.subtitle}
              </h3>
              <p className="mt-6 text-base font-body text-gray-700 sm:text-lg">
                {heroData.section.text}
              </p>
            </header>
          </div>

          <figure className="w-full max-w-6xl mx-auto mt-12 mb-10">
            <div className="h-[60vh] w-full overflow-hidden rounded-xl shadow-md">
              <img
                src={heroData.section.gridBanner}
                alt="Grid Banner"
                width="1920"
                height="1080"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/placeholder-profile.png";
                }}
                className="w-full h-full object-cover"
              />
            </div>
          </figure>
          <section className="w-full max-w-6xl mx-auto min-h-[30vh] flex flex-col items-center justify-center px-6 text-center mt-6 mb-4">
            <div className="w-full">
              <h3 className="mt-4 text-balance text-2xl font-semibold tracking-tight text-gray-950 font-heading sm:text-3xl">
                {heroData.section.introTitle}
              </h3>
              <p className="mt-2 text-base text-gray-700 font-body sm:text-lg">
                {heroData.section.introText}
              </p>
            </div>
          </section>

          {/* Lazy Loaded Bento Grid */}
          <nav aria-label="Hauptnavigation" className="max-w-6xl mx-auto mt-8 sm:mt-10 px-4">
            <Suspense fallback={<div className="text-center py-8">Loading menu...</div>}>
              <BentoGrid>
                {items.map((item, i) => (
                  <a href={item.link} key={i}>
                    <BentoGridItem
                      title={item.title}
                      description={item.description}
                      header={item.header}
                      icon={item.icon}
                      className={i === 3 || i === 6 ? "md:col-span-2" : ""}
                    />
                  </a>
                ))}
              </BentoGrid>
            </Suspense>
          </nav>

        </div>
      </section>
    </main>
  );
};

export default Hero;
